import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface SporesEffectProps {
  intensity?: number;
  particleCount?: number;
  areaSize: [number, number];
}

export function SporesEffect({
  intensity = 0.7,
  particleCount = 80, // Reduced from 120
  areaSize,
}: SporesEffectProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const count = Math.floor(particleCount * intensity);

  // Stabilize areaSize
  const areaSizeKey = `${areaSize[0]}-${areaSize[1]}`;

  // Create petal data
  const petals = useMemo(() => {
    const data: {
      position: THREE.Vector3;
      rotation: THREE.Euler;
      scale: number;
      opacity: number;
      phase: number;
      rotationSpeed: THREE.Vector3;
      grayShade: number;
    }[] = [];

    for (let i = 0; i < count; i++) {
      const grayShade = 0.3 + Math.random() * 0.4;

      data.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * areaSize[0],
          Math.random() * 8,
          (Math.random() - 0.5) * areaSize[1]
        ),
        rotation: new THREE.Euler(
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2,
          Math.random() * Math.PI * 2
        ),
        scale: 0.15 + Math.random() * 0.25,
        opacity: 0.3 + Math.random() * 0.4,
        phase: Math.random() * Math.PI * 2,
        rotationSpeed: new THREE.Vector3(
          (Math.random() - 0.5) * 0.5,
          (Math.random() - 0.5) * 0.5,
          (Math.random() - 0.5) * 0.5
        ),
        grayShade,
      });
    }

    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, areaSizeKey]);

  // Simple petal geometry - low poly
  const geometry = useMemo(() => {
    const geo = new THREE.PlaneGeometry(0.4, 0.5, 1, 1);
    return geo;
  }, []);

  // Shared material
  const material = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color: 0x666666,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
  }, []);

  // Matrix for instance transformations
  const tempMatrix = useMemo(() => new THREE.Matrix4(), []);
  const tempQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const tempEuler = useMemo(() => new THREE.Euler(), []);

  // Initialize instance positions
  useEffect(() => {
    if (!meshRef.current) return;

    petals.forEach((petal, i) => {
      tempEuler.set(petal.rotation.x, petal.rotation.y, petal.rotation.z);
      tempQuaternion.setFromEuler(tempEuler);
      tempMatrix.compose(
        petal.position,
        tempQuaternion,
        new THREE.Vector3(petal.scale, petal.scale, petal.scale)
      );
      meshRef.current!.setMatrixAt(i, tempMatrix);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [petals, tempMatrix, tempQuaternion, tempEuler]);

  // Animation using InstancedMesh - much more performant
  useFrame((state) => {
    if (!meshRef.current) return;

    const time = state.clock.elapsedTime;

    petals.forEach((petal, i) => {
      const baseY = ((time * 0.1 + petal.phase) % 10) - 1;

      const x = petal.position.x + Math.sin(time * 0.2 + petal.phase) * 0.3;
      const y = baseY + Math.sin(time * 0.15 + petal.phase * 2) * 0.2;
      const z = petal.position.z + Math.cos(time * 0.18 + petal.phase) * 0.3;

      const rx = petal.rotation.x + time * petal.rotationSpeed.x * 0.3;
      const ry = petal.rotation.y + time * petal.rotationSpeed.y * 0.3;
      const rz = petal.rotation.z + time * petal.rotationSpeed.z * 0.3;

      tempEuler.set(rx, ry, rz);
      tempQuaternion.setFromEuler(tempEuler);
      tempMatrix.compose(
        new THREE.Vector3(x, y, z),
        tempQuaternion,
        new THREE.Vector3(petal.scale, petal.scale, petal.scale)
      );
      meshRef.current!.setMatrixAt(i, tempMatrix);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, count]}
      frustumCulled={false}
    />
  );
}
